/**
 * A theory answer, between the rich editor and the marker.
 *
 * The editor works on a document: paragraphs of text, line breaks, lists and
 * equations. What is stored and sent to the marker is plain text in the bank's
 * own format, so the marker, the 20,000-character cap and every existing
 * screen read it unchanged:
 *
 *  - paragraphs are separated by a blank line;
 *  - a line break inside a paragraph is a new line;
 *  - a list item starts a line with `- ` (bullets) or `1. ` (numbered);
 *  - an equation is `\(latex\)`, chemistry `\(\ce{...}\)`.
 *
 * `toAnswer` and `fromAnswer` are exact inverses for every document the editor
 * can produce (property-tested over thousands of generated answers), so a draft
 * restored from storage is the answer the student wrote.
 *
 * Anything a student types that would read back as structure is escaped with a
 * backslash: a backslash itself, a typed `\(`, and a line that would otherwise
 * start a list or end a paragraph. A line holding only spaces is written as
 * `\ ` for the same reason — on the way back a truly empty line is always a
 * paragraph break.
 */

export type Inline =
  { type: "text"; text: string } | { type: "math"; latex: string } | { type: "break" };

export type Block =
  { type: "paragraph"; content: Inline[] } | { type: "list"; ordered: boolean; items: Inline[][] };

export interface AnswerDoc {
  blocks: Block[];
}

/** The backend's limit on a theory answer (`MarkTheoryBody.answer`). */
export const ANSWER_MAX = 20_000;

export const emptyDoc = (): AnswerDoc => ({ blocks: [] });

/** The prefix that marks a list line, numbered or not. */
const LIST_START = /^(?:- |\d+\. )/;

// ── Writing ─────────────────────────────────────────────────────────

/** Text with its backslashes doubled, so none can be read as an equation opener. */
const escapeText = (text: string) => text.replace(/\\/g, "\\\\");

/** One block's content as lines, before the line guards. */
function linesOf(content: Inline[]): string[] {
  const lines: string[] = [""];
  for (const node of content) {
    if (node.type === "break") lines.push("");
    else
      lines[lines.length - 1] +=
        node.type === "text" ? escapeText(node.text) : `\\(${node.latex}\\)`;
  }
  return lines;
}

/**
 * A line that would read back as something else gets a leading backslash: an
 * empty line (a paragraph break), a list marker, or a line that already starts
 * with the guard. Single backslashes never start text otherwise, because text
 * backslashes are doubled and equations start `\(`.
 */
function guard(line: string): string {
  if (line === "") return "\\";
  if (LIST_START.test(line) || /^\\(?![\\(])/.test(line)) return "\\" + line;
  return line;
}

/**
 * The document as the editor should hold it: a line break inside a text node
 * (from a paste, say) becomes a break node, `\r\n` counts once, and adjacent
 * text is merged. `toAnswer` writes this form, so what's stored always reads
 * back to exactly the document the student sees.
 */
export function normaliseDoc(doc: AnswerDoc): AnswerDoc {
  const inline = (content: Inline[]): Inline[] => {
    const out: Inline[] = [];
    const pushText = (text: string) => {
      if (!text) return;
      const last = out[out.length - 1];
      if (last?.type === "text") last.text += text;
      else out.push({ type: "text", text });
    };
    for (const node of content) {
      if (node.type !== "text") {
        out.push({ ...node });
        continue;
      }
      node.text.split(/\r\n?|\n/).forEach((part, index) => {
        if (index > 0) out.push({ type: "break" });
        pushText(part);
      });
    }
    return out;
  };
  return {
    blocks: doc.blocks.map((block) =>
      block.type === "paragraph"
        ? { type: "paragraph", content: inline(block.content) }
        : { type: "list", ordered: block.ordered, items: block.items.map(inline) },
    ),
  };
}

export function toAnswer(doc: AnswerDoc): string {
  return normaliseDoc(doc)
    .blocks.map((block) => {
      if (block.type === "paragraph") return linesOf(block.content).map(guard).join("\n");
      return block.items
        .map((item, index) => {
          const marker = block.ordered ? `${index + 1}. ` : "- ";
          const [first = "", ...rest] = linesOf(item);
          // Continuation lines are indented under the item, and guarded.
          return [marker + first, ...rest.map((line) => "   " + guard(line))].join("\n");
        })
        .join("\n");
    })
    .join("\n\n");
}

// ── Reading ─────────────────────────────────────────────────────────

/** Remove a line's guard backslash, if `guard` added one. */
function unguard(line: string): string {
  if (line === "\\") return "";
  if (/^\\(?![\\(])/.test(line)) return line.slice(1);
  return line;
}

/** Where `\)` closes an equation whose body starts at `from`, or -1. */
function findClose(source: string, from: number): number {
  for (let i = from; i < source.length - 1; i += 1) {
    if (source[i] === "\\" && source[i + 1] === ")") return i;
    // A backslash command inside the equation skips its next character.
    if (source[i] === "\\") i += 1;
  }
  return -1;
}

/** One line's text and equations, undoing `escapeText`. */
function parseLine(source: string, out: Inline[]): void {
  let text = "";
  const flush = () => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last?.type === "text") last.text += text;
    else out.push({ type: "text", text });
    text = "";
  };
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i]!;
    if (char === "\\" && source[i + 1] === "\\") {
      text += "\\";
      i += 1;
    } else if (char === "\\" && source[i + 1] === "(") {
      const end = findClose(source, i + 2);
      if (end < 0) {
        // An unclosed opener (typed in the plain box) stays text, as typed.
        text += source.slice(i);
        break;
      }
      flush();
      out.push({ type: "math", latex: source.slice(i + 2, end) });
      i = end + 1;
    } else {
      text += char;
    }
  }
  flush();
}

/** Lines into inline content, a break between each. */
function parseLines(lines: string[]): Inline[] {
  const out: Inline[] = [];
  lines.forEach((line, index) => {
    if (index > 0) out.push({ type: "break" });
    parseLine(line, out);
  });
  return out;
}

function parseList(lines: string[]): Block {
  const ordered = !lines[0]!.startsWith("- ");
  const items: string[][] = [];
  for (const line of lines) {
    if (LIST_START.test(line)) items.push([line.replace(LIST_START, "")]);
    else items[items.length - 1]!.push(unguard(line.replace(/^ {3}/, "")));
  }
  return { type: "list", ordered, items: items.map(parseLines) };
}

export function fromAnswer(answer: string): AnswerDoc {
  if (!answer) return emptyDoc();
  const blocks = answer
    .replace(/\r\n?/g, "\n")
    .split("\n\n")
    .map((chunk): Block => {
      const lines = chunk.split("\n");
      if (LIST_START.test(lines[0]!)) return parseList(lines);
      return { type: "paragraph", content: parseLines(lines.map(unguard)) };
    });
  return { blocks };
}

/** Every equation in the answer, in order: what the validator checks. */
export function equationsOf(doc: AnswerDoc): string[] {
  const all: string[] = [];
  const visit = (content: Inline[]) => {
    for (const node of content) if (node.type === "math") all.push(node.latex);
  };
  for (const block of doc.blocks) {
    if (block.type === "paragraph") visit(block.content);
    else block.items.forEach(visit);
  }
  return all;
}
